import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { store, useDB } from '../data/store';
import { isFollowing, me, search, userByHandle, userById, visiblePosts } from '../data/queries';
import { Composer, PostCard, Thread } from '../components/Post';
import { Avatar, ConfirmButton, ExampleLabel, Rainbow, TagLink, YearLink, timeAgo } from '../components/Bits';
import { useUI } from '../components/ui';
import { FeedView } from './Feeds';
import { NotFound } from './Static';
import { BRANCHES, type Branch } from '../lib/validation';
import { isYear } from '../lib/years';

export function PostPage() {
  const { id } = useParams();
  const db = useDB();
  const post = db.posts.find(p => p.id === id && !p.removed);
  if (!post) return <NotFound what="This memory was deleted or never existed." />;
  const passed = post.passedFromId ? db.posts.find(p => p.id === post.passedFromId) : undefined;
  const passedOn = visiblePosts(db).filter(p => p.passedFromId === post.id);
  return (
    <div className="narrow col">
      <PostCard post={post} full />
      {passed && <p className="small muted">Passed on from <Link to={`/p/${passed.id}`}>{passed.title}</Link></p>}
      {post.branch === 'Kindness' && <p className="small muted">This kindness was passed on {passedOn.length} {passedOn.length === 1 ? 'time' : 'times'}.</p>}
      <Thread post={post} />
    </div>
  );
}

export function Profile() {
  const { handle = '' } = useParams();
  const db = useDB(), ui = useUI();
  const user = userByHandle(db, handle);
  const filter = useMemo(() => ({ authorId: user?.id }), [user?.id]);
  if (!user) return <NotFound what={`Nobody called @${handle} here.`} />;
  const mine = user.id === db.meId;
  const posts = visiblePosts(db).filter(p => p.authorId === user.id);
  const years = [...new Set(posts.map(p => p.year))].sort();
  const followers = db.follows.filter(f => f.kind === 'user' && f.target === user.id).length;
  const following = isFollowing(db, 'user', user.id);
  return (
    <div className="narrow col">
      <div className="card profile" style={{ ['--uc' as string]: `var(--rb-${user.color})` }}>
        <div className="row"><Avatar name={user.handle} color={user.color} /><div>
          <h1 className="pname" style={{ color: `var(--rb-${user.color})` }}>{user.name}</h1>
          <p className="muted">@{user.handle} · joined {timeAgo(user.joinedAt)} {user.example && <ExampleLabel />}</p></div></div>
        {user.away && <p className="away">Away: {user.away}</p>}
        {user.bio && <p>{user.bio}</p>}
        <p className="small muted">{posts.length} memories · {followers} followers{years.length > 0 && <> · years: {years.map(y => <span key={y}> <YearLink year={y} /></span>)}</>}</p>
        <div className="row">
          {mine ? <><button className="btn ghost" onClick={ui.askProfile}>Edit profile</button><Link className="btn ghost" to="/settings">Settings</Link></>
            : <><button className="btn hot" aria-pressed={following} onClick={() => ui.act(() => store.toggleFollow('user', user.id))}>{following ? 'Following' : 'Follow'}</button>
              <Link className="btn ghost" to="/messages">Message</Link>
              <button className="btn ghost" onClick={() => ui.report('user', user.id)}>Report</button></>}
        </div>
      </div>
      <FeedView filter={filter} empty={mine ? 'You haven\'t posted yet. Share your first memory.' : 'No memories yet.'} endText="That's everything." showBranches={false} />
    </div>
  );
}

export function SearchPage() {
  const db = useDB();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const r = useMemo(() => search(db, q), [db, q]);
  const nav = useNavigate();
  const y = Number(q.trim());
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Search</span><h1>Find a <Rainbow text="memory" />.</h1></div>
      <label className="fld"><span>Search memories, people, #hashtags, communities and ideas</span>
        <input type="search" value={q} onChange={e => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })} placeholder="snow day, #flashgames, @sunburnt, 2009" autoFocus /></label>
      {isYear(y) && <button className="btn ghost" onClick={() => nav(`/y/${y}`)}>Go to {y}</button>}
      {q.trim().length >= 2 && <>
        {r.tags.length > 0 && <section><h2 className="h2">Hashtags</h2><div className="tags">{r.tags.map(t => <TagLink key={t.tag} tag={t.tag} />)}</div></section>}
        {r.users.length > 0 && <section><h2 className="h2">People</h2><ul className="list">{r.users.map(u => <li key={u.id}><Link to={`/u/${u.handle}`} style={{ color: `var(--rb-${u.color})` }}>@{u.handle}</Link> <span className="muted">{u.name}</span></li>)}</ul></section>}
        {r.communities.length > 0 && <section><h2 className="h2">Communities</h2><ul className="list">{r.communities.map(c => <li key={c.id}><Link to={`/c/${c.slug}`}>{c.name}</Link></li>)}</ul></section>}
        {r.ideas.length > 0 && <section><h2 className="h2">Ideas</h2><ul className="list">{r.ideas.map(i => <li key={i.id}><Link to={`/ideas/${i.id}`}>{i.text}</Link></li>)}</ul></section>}
        <section><h2 className="h2">Memories</h2>{r.posts.length ? <div className="feed">{r.posts.map(p => <PostCard key={p.id} post={p} />)}</div> : <p className="muted">No memories match "{q}".</p>}</section>
      </>}
    </div>
  );
}

export function Notifications() {
  const db = useDB(), ui = useUI();
  const user = me(db);
  const list = user ? db.notifications.filter(n => n.userId === user.id) : [];
  const unread = list.filter(n => !n.read).length;
  useEffect(() => { if (unread) store.markNotificationsRead(); }, [unread]);
  const words = { reaction: 'remembered your memory', comment: 'commented on your memory', reply: 'replied to you', follow: 'followed you', answer: 'answered your idea' };
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Notifications</span><h1>What <Rainbow text="happened" /> while you were away.</h1></div>
      {!user ? <div className="empty"><p>Pick a name to get notifications.</p><button className="btn hot" onClick={ui.askProfile}>Pick a name</button></div>
        : list.length === 0 ? <div className="empty">Nothing yet. When people remember, comment on or answer your posts, it shows up here. In the preview only you are here, so this fills up once accounts launch.</div>
        : <ul className="list">{list.map(n => { const a = userById(db, n.actorId); return (
          <li key={n.id} className={'card notif' + (n.read ? '' : ' unread')}><Link to={`/u/${a?.handle}`}>@{a?.handle}</Link> {words[n.type]}{n.postId && <> · <Link to={`/p/${n.postId}`}>open</Link></>} <span className="muted small">{timeAgo(n.createdAt)}</span></li>); })}</ul>}
      <p className="small muted">You'll be able to choose which notifications you get, and set quiet hours, in Settings once accounts launch.</p>
    </div>
  );
}

export function Messages() {
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Messages</span><h1>Private <Rainbow text="messages" /> are coming.</h1>
        <p>Messages need real accounts so only the two of you can read them. They arrive with accounts (plan stage 8), with message requests from people you don't follow, blocking and reporting built in.</p></div>
      <Link className="btn ghost" to="/">Back to memories</Link>
    </div>
  );
}

export function NewPost() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const year = Number(params.get('year'));
  const branch = params.get('branch');
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">New memory</span><h1>What do you <Rainbow text="remember" />?</h1></div>
      <Composer key={params.toString()} onPosted={p => nav(`/p/${p.id}`)} defaults={{
        year: isYear(year) ? year : undefined, title: params.get('title') ?? undefined,
        branch: branch && (BRANCHES as readonly string[]).includes(branch) ? branch as Branch : undefined,
        passedFromId: params.get('passed') ?? undefined }} />
    </div>
  );
}

export function Settings() {
  const db = useDB(), ui = useUI();
  const s = db.settings, user = me(db);
  const [exported, setExported] = useState('');
  const [confirm, setConfirm] = useState('');
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Settings</span><h1>Make it <Rainbow text="yours" />.</h1></div>
      <div className="card form">
        <h2 className="h2">Look</h2>
        <label className="chk"><input type="checkbox" checked={s.mode === 'light'} onChange={e => store.setSettings({ mode: e.target.checked ? 'light' : 'dark' })} />Lights on (light mode)</label>
        <label className="chk"><input type="checkbox" checked={s.yearFonts} onChange={e => store.setSettings({ yearFonts: e.target.checked })} />Write each year in its own era font</label>
        <label className="chk"><input type="checkbox" checked={s.stickers} onChange={e => store.setSettings({ stickers: e.target.checked })} />Show stickers at the edges</label>
        <label className="chk"><input type="checkbox" checked={s.hideExamples} onChange={e => store.setSettings({ hideExamples: e.target.checked })} />Hide the example posts</label>
      </div>
      <div className="card form">
        <h2 className="h2">Your data</h2>
        {user ? <>
          <p>Signed in on this browser as <b style={{ color: `var(--rb-${user.color})` }}>@{user.handle}</b>.</p>
          <div className="row"><button className="btn ghost" onClick={ui.askProfile}>Edit profile</button><button className="btn ghost" onClick={() => setExported(store.exportMine())}>Show all my data</button></div>
          {exported && <textarea className="in mono" rows={8} readOnly value={exported} aria-label="All your data as JSON" />}
          <label className="fld"><span>Delete my profile and everything I posted. Type DELETE to confirm.</span><input value={confirm} onChange={e => setConfirm(e.target.value)} /></label>
          <button className="btn danger" disabled={confirm !== 'DELETE'} onClick={() => { store.deleteMe(); setConfirm(''); ui.toast('Your profile and posts are deleted'); }}>Delete everything</button>
        </> : <><p>You haven't picked a name yet.</p><button className="btn hot" onClick={ui.askProfile}>Pick a name</button></>}
      </div>
      <div className="card form">
        <h2 className="h2">Preview</h2>
        <p className="small muted">Everything here lives in this browser only. Reset brings back the examples and removes your preview posts.</p>
        <ConfirmButton className="btn ghost" label="Reset the preview" confirmLabel="Tap again to reset" onConfirm={() => { store.resetPreview(); ui.toast('Preview reset'); }} />
      </div>
    </div>
  );
}
